import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeErpDataTableViewRow } from '@/lib/erp-data-table-events';
import { getPermissionResourceKeyFromPath } from '@/lib/permissions';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/lib/hot-toast';

/**
 * Hook for managing form state
 * @template T The type of the selected object (must have pt_tao_yn property)
 * @template K The type of the row ID (string | number)
 * @returns Form state and functions to manage it
 */
type FetchDetailFn = (uuid: string) => Promise<any>;

export const useForms = <T extends { uuid: string } = any>(fetchDetail?: FetchDetailFn) => {
  const { permissions, isAdmin } = useAuth();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showBulkDelete, setShowBulkDelete] = useState<boolean>(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedRowIndex, setSelectedRowIndex] = useState<any | null>(null);
  const [selectedObj, setSelectedObj] = useState<T | null>(null);

  const [isCopyMode, setIsCopyMode] = useState<boolean>(false);
  const loadedDetailUuidRef = useRef<string | null>(null);
  const loadingDetailsRef = useRef(new Map<string, Promise<void>>());

  const getPermission = (action: 'create' | 'update' | 'delete') => {
    if (isAdmin) return true;
    const resourceKey = getPermissionResourceKeyFromPath(window.location.pathname);
    return resourceKey ? permissions[resourceKey]?.[action] === true : false;
  };

  const loadDetailIfNeeded = useCallback(
    async (uuid?: string | null) => {
      if (!fetchDetail || !uuid) return;
      if (loadedDetailUuidRef.current === uuid) return;

      const loadingDetail = loadingDetailsRef.current.get(uuid);
      if (loadingDetail) {
        await loadingDetail;
        return;
      }

      const request = fetchDetail(uuid)
        .then(() => {
          loadedDetailUuidRef.current = uuid;
        })
        .finally(() => {
          loadingDetailsRef.current.delete(uuid);
        });

      loadingDetailsRef.current.set(uuid, request);
      await request;
    },
    [fetchDetail]
  );

  /**
   * Handle closing the form
   */
  const handleCloseForm = () => {
    setShowForm(false);
  };

  /**
   * Handle closing the delete confirmation dialog
   */
  const handleCloseDelete = () => {
    setShowDelete(false);
  };

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        if (!getPermission('delete')) {
          toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
          return;
        }
        setShowBulkDelete(true);
      }
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, [permissions, isAdmin]);

  /**
   * Handle closing the bulk delete confirmation dialog
   */
  const handleCloseBulkDelete = () => {
    setShowBulkDelete(false);
  };

  /**
   * Handle clicking the add button
   */
  const handleAddClick = () => {
    if (!getPermission('create')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setFormMode('add');
    setIsCopyMode(false);
    setShowForm(true);
  };

  /**
   * Handle clicking the edit button
   */
  const handleEditClick = async () => {
    if (!getPermission('update')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    if (!selectedObj?.uuid) return;

    await loadDetailIfNeeded(selectedObj.uuid);

    setFormMode('edit');
    setShowForm(true);
  };

  /**
   * Handle clicking the view button
   */
  const handleViewClick = useCallback(
    async (row?: any) => {
      const target = row ?? selectedObj;

      if (!target?.uuid) return;

      if (row) {
        setSelectedObj(row);
        setSelectedRowIndex(row.uuid);
      }

      await loadDetailIfNeeded(target?.uuid);

      setFormMode('view');
      setShowForm(true);
    },
    [loadDetailIfNeeded, selectedObj]
  );

  useEffect(
    () =>
      subscribeErpDataTableViewRow(params => {
        return handleViewClick(params.row);
      }),
    [handleViewClick]
  );

  /**
   * Handle clicking the delete button
   */
  const handleDeleteClick = () => {
    if (!getPermission('delete')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setShowDelete(true);
  };

  /**
   * Handle clicking the copy button
   */
  const handleCopyClick = async () => {
    if (!getPermission('create')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    if (!selectedObj?.uuid) return;

    await loadDetailIfNeeded(selectedObj.uuid);

    setFormMode('add');
    setIsCopyMode(true);
    setShowForm(true);
  };

  /**
   * Handle clicking a row in the data table
   * @param params The row parameters containing row data and id
   */
  const handleRowClick = async (params: { id: string; row: any }) => {
    setSelectedRowIndex(params.id);
    setSelectedObj(params.row);
    await loadDetailIfNeeded(params.row.uuid);
  };

  const handleSearch = () => {
    setShowSearch(true);
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
  };

  const clearSelection = () => {
    setSelectedRowIndex(null);
    setSelectedObj(null);
    loadedDetailUuidRef.current = null;
    loadingDetailsRef.current.clear();
  };

  const handleOpenImportExcel = () => {
    if (!getPermission('create')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setIsImportDialogOpen(true);
  };

  return {
    showForm,
    showSearch,
    showDelete,
    isImportDialogOpen,
    setIsImportDialogOpen,
    formMode,
    selectedRowIndex,
    isCopyMode,
    selectedObj,
    showBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleCloseBulkDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleSearch,
    handleCloseSearch,
    setShowBulkDelete,
    handleOpenImportExcel
  };
};
